'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import TrailerCarouselImageOverlay from '@/app/[locale]/(worlds)/[roomId]/components/TrailerCarouselImageOverlay';
import ViverseVideoPlayer from '@/components/ViverseVideoPlayer';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useTrailerViewMediaContext } from '@/context/TrailerViewMediaContext';
import { useUAContext } from '@/context/UAContext';
import { useBlendVisionSources } from '@/hooks/useBlendVisionSources';

// Module-level: stable reference prevents duplicate visibilitychange listeners if BlendVision uses modulesConfig as a useEffect dep.
// No removeEventListener: normalizeTabSwitch has no cleanup hook; detached mediaElement makes calls no-ops after player destroy.
const modulesConfigWithResume = {
  normalizeTabSwitch: (mediaElement: HTMLVideoElement) => {
    document.addEventListener('visibilitychange', () => {
      setTimeout(() => {
        if (document.hidden) {
          mediaElement.pause();
        } else {
          mediaElement.play().catch(() => {});
        }
      }, 50);
    });
  }
};
interface RenderVideoProps {
  url: string;
  playbackToken?: string;
  dashUri?: string;
  hlsUri?: string;
  title?: string;
  coverImage?: string;
  onEnded: () => void;
}
const TrailerCarouselVideo = t0 => {
  const $ = _c(23);
  const {
    url,
    playbackToken,
    dashUri,
    hlsUri,
    title,
    coverImage,
    onEnded
  } = t0;
  const {
    isPremiumRoom,
    roomData
  } = useCheckEnterRoomContext();
  const {
    isMobile
  } = useUAContext();
  const {
    backgroundPlay,
    containerClassName
  } = useTrailerViewMediaContext();
  const shouldShowControls = !backgroundPlay && (isPremiumRoom || !isMobile);
  const shouldShowBlendVisionControls = shouldShowControls;
  let t1;
  if ($[0] !== dashUri || $[1] !== hlsUri || $[2] !== playbackToken) {
    t1 = playbackToken && dashUri && hlsUri ? {
      dashUri,
      hlsUri,
      playbackToken
    } : undefined;
    $[0] = dashUri;
    $[1] = hlsUri;
    $[2] = playbackToken;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const videoData = t1;
  const memoizedVideoSources = useBlendVisionSources(videoData);
  if (playbackToken && memoizedVideoSources) {
    const t2 = backgroundPlay ? modulesConfigWithResume : undefined;
    let t3;
    if ($[4] !== backgroundPlay || $[5] !== containerClassName || $[6] !== coverImage || $[7] !== memoizedVideoSources || $[8] !== shouldShowBlendVisionControls || $[9] !== t2 || $[10] !== title) {
      t3 = <ViverseVideoPlayer controls={shouldShowBlendVisionControls} title={title} poster={coverImage} playbackState="playing" source={memoizedVideoSources} autoPlay={backgroundPlay} hideUI={backgroundPlay} containerClassName={containerClassName} modulesConfig={t2} />;
      $[4] = backgroundPlay;
      $[5] = containerClassName;
      $[6] = coverImage;
      $[7] = memoizedVideoSources;
      $[8] = shouldShowBlendVisionControls;
      $[9] = t2;
      $[10] = title;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    let t4;
    if ($[12] !== backgroundPlay || $[13] !== roomData?.image) {
      t4 = backgroundPlay && <TrailerCarouselImageOverlay url={roomData?.image} />;
      $[12] = backgroundPlay;
      $[13] = roomData?.image;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t3 || $[16] !== t4) {
      t5 = <div className="relative size-full rounded-none bg-gray-500/10 object-cover md:rounded-[5px] lg:rounded-[15px]">{t3}{t4}</div>;
      $[15] = t3;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  } else {
    if (url) {
      let t2;
      if ($[18] !== coverImage || $[19] !== onEnded || $[20] !== shouldShowControls || $[21] !== url) {
        t2 = <video autoPlay={true} muted={true} playsInline={true} onEnded={onEnded} controls={shouldShowControls} src={url} poster={coverImage} className="h-full w-full object-cover" />;
        $[18] = coverImage;
        $[19] = onEnded;
        $[20] = shouldShowControls;
        $[21] = url;
        $[22] = t2;
      } else {
        t2 = $[22];
      }
      return t2;
    } else {
      return null;
    }
  }
};
export default TrailerCarouselVideo;